import React, { useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { Theme } from '@mui/material/styles';
import styled from '@emotion/styled';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Box,
  Stack,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import colors from '../Theme/colors';

const StyledCard = styled(Card)`
  max-width: 28rem;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
`;

const StyledCardHeader = styled(CardHeader)`
  padding: 30px 40px 20px;
  & .MuiCardHeader-content {
    margin: 0 auto
  }
`;

const HeaderTypography = styled(Typography)`
  font-weight: 800;
  font-size: 1.3rem;
  text-align: center;
  line-height: 1.3;
`;

const Ribbon = styled.div<{ theme?: Theme }>`
  position: absolute;
  top: 0;
  right: 0;
  margin-right: -56px;
  margin-top: 20px;
  transform: rotate(45deg);
  z-index: 10;
  background-color: #e53935;
  color: ${colors.white};
  padding: 4px 48px;
  font-size: 0.75rem;
  font-weight: 600;
  box-shadow: ${({ theme }) => theme.shadows[2]};
`;

const CheckItem = styled('div')`
  margin-bottom: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const CheckIconWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: rgba(76, 175, 80, 0.1);
`;

const StyledButton = styled(Button)<{ theme?: Theme }>`
  width: 100%;
  background-color: ${colors.black};
  color: ${colors.white};
  padding: 12px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  text-transform: none;
  &:hover {
    background-color: rgba(0, 0, 0, 0.9);
    transform: scale(1.01);
    box-shadow: ${({ theme }) => theme.shadows[4]};
  }
`;

const ButtonContent = styled.div<{ theme?: Theme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const StrikethroughText = styled.span`
  text-decoration: line-through;
`;

const ButtonGlow = styled.div<{ isHovering: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  ${'' /* eslint-disable-next-line max-len */}
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.11), transparent);
  transition: all 1.5s;
  transform: ${
  ({ isHovering }) => (isHovering ? 'translateX(100%)' : 'translateX(-100%)')
}
`;

const InfoText = styled(Typography)`
  margin-top: 8px;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
`;

const CenteredInfoText = styled(InfoText)`
  text-align: center;
  margin-bottom: 8px;
`;

const sellingPointPropertyTypes = {
  sellingPointMessage: PropTypes.string.isRequired,
};

type SellingPointProperties = InferProps<
typeof sellingPointPropertyTypes
>;

const SellingPoint = ({ sellingPointMessage }: SellingPointProperties) => (
  <CheckItem>
    <CheckIconWrapper>
      <CheckIcon sx={{ color: '#4caf50', fontSize: '1.25rem' }} />
    </CheckIconWrapper>
    <Typography>
      {sellingPointMessage}
    </Typography>
  </CheckItem>
);

const subscriptionCallToActionPropertyTypes = {
  callToActionMessage: PropTypes.string.isRequired,
  saveMessage: PropTypes.string.isRequired,
  sellingPoints: PropTypes.arrayOf(
    PropTypes.string.isRequired,
  ).isRequired,
  youCanCancelMessage: PropTypes.string.isRequired,
  startReadingMessage: PropTypes.string.isRequired,
  complimentaryAccessMessage: PropTypes.string.isRequired,
  subscriptionPriceMessage: PropTypes.string.isRequired,
  subscriptionBillingPeriodMessage: PropTypes.string.isRequired,
  LinkComponent: PropTypes.elementType.isRequired,
  onClick: PropTypes.func.isRequired,
};

type SubscriptionCallToActionProperties = InferProps<
  typeof subscriptionCallToActionPropertyTypes
>;

const SubscriptionCallToAction = ({
  callToActionMessage,
  saveMessage,
  sellingPoints,
  youCanCancelMessage,
  startReadingMessage,
  complimentaryAccessMessage,
  subscriptionPriceMessage,
  subscriptionBillingPeriodMessage,
  LinkComponent,
  onClick,
}: SubscriptionCallToActionProperties) => {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <StyledCard>
      <Ribbon>
        { saveMessage }
        {' '}
        16%
      </Ribbon>
      <StyledCardHeader
        title={(
          <HeaderTypography>
            { callToActionMessage }
          </HeaderTypography>
        )}
      />
      <CardContent sx={{ pt: 2 }}>
        <Stack spacing={3}>
          <Box>
            {sellingPoints.map((sellingPoint: string) => (
              <SellingPoint
                key={sellingPoint}
                sellingPointMessage={sellingPoint}
              />
            ))}
          </Box>

          <Box>
            <CenteredInfoText>
              { youCanCancelMessage }
            </CenteredInfoText>
            <LinkComponent route="subscribe">
              <StyledButton
                variant="contained"
                size="large"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onClick={onClick}
                data-testid={
                  `subscription-button-${isHovering ? 'hovering' : 'default'}`
                }
              >
                <ButtonContent>
                  { startReadingMessage }
                  <StrikethroughText>€18</StrikethroughText>
                  €15
                  <ArrowForwardIcon
                    sx={{
                      fontSize: '1rem',
                      opacity: isHovering ? 1 : 0,
                      transform: isHovering ? 'translate(4px)' : 'translate(0)',
                      transition: 'all 0.1s',
                    }}
                  />
                </ButtonContent>
                <ButtonGlow isHovering={isHovering} />
              </StyledButton>
            </LinkComponent>
          </Box>

          <Box>
            <InfoText>
              { complimentaryAccessMessage }
            </InfoText>
            <InfoText sx={{ marginTop: 1 }}>
              { subscriptionPriceMessage }
              {' '}
              <StrikethroughText>€18</StrikethroughText>
              {' '}
              €15
              {' '}
              { subscriptionBillingPeriodMessage }
            </InfoText>
          </Box>
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

export default SubscriptionCallToAction;
